import { Inject } from '@odinjs/odin';
import { CardContainerAPI, DatePickerAPI, DeckAPI, MapperAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnChange, OnMount, OnSelect } from '@philips-internal/oswald-ui-core/lib/client';
import { FragmentNames, PseudonymAppointmentsStepCheckupSuggestionsAliases } from '../../codegen';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { CheckupSuggestion, CheckupSuggestionGroups, ScheduleItem } from '../../interfaces/schedules-suggestions';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ChangeEvent, MountEvent, SelectEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { Value } from '@philips-internal/oswald-ui-core';
import './pseudonym-appointments-step-checkup-suggestions-controller.css';
import DateFormatterUtil from '../../utils/date-utils/date-formatter-util';
import { AppointmentsSteps, ScheduleType } from './pseudonym-types';
import moment from 'moment';

@Controller({ component: PseudonymAppointmentsStepCheckupSuggestionsAliases.LAYOUT_SUGGESTION, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_CHECKUP_SUGGESTIONS })
export default class PseudonymAppointmentsStepCheckupSuggestionsController extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.PANEL_SUGGESTION_LIST)
    panelSuggestionListApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.DATE_PICKER_SCHEDULE_RANGE)
    datePickerCheckupScheduleRangeApi!: DatePickerAPI;

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.PANEL_LOADER)
    panelLoaderApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.MAPPER_CHECKUP_SUGGESTION)
    checkupSuggestionMapperApi!: MapperAPI;

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.DECK_SUGGESTION_GROUPS)
    checkupSuggestionDeckGroupsApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStepCheckupSuggestionsAliases.DECK_SUGGESTION_GROUPS)
    checkupSuggestionDeckApi!: DeckAPI;

  @OnMount({ target: PseudonymAppointmentsStepCheckupSuggestionsAliases.LAYOUT_SUGGESTION })
  handleMountLayoutSuggestion(): void {
    PseudonymAppointmentModel.stepCheckupSuggestions.populateCheckupSuggestion = () => this.populateCheckupSuggestion();
  }

  populateCheckupSuggestion(): void {
    this.panelSuggestionListApi.setVisible(false);
    this.panelLoaderApi.setVisible(true);

    this.getAvailableSuggestions()
      .finally(() => this.panelLoaderApi.setVisible(false));
  }

  private getAvailableSuggestions(): Promise<void> {
    const dataRange = PseudonymAppointmentModel.stepSix.selectedDateRange;
    const healthInsurance = PseudonymAppointmentModel.stepOne.healthInsurance;
    const body = {
      categoryCode: healthInsurance.categoryId ?? '',
      checkupType: PseudonymAppointmentModel.stepCheckupSuggestions.checkupType,
      finalDate: DateFormatterUtil.dateISOWithoutUTC(dataRange.end),
      initialDate: DateFormatterUtil.dateISOWithoutUTC(dataRange.start),
      insuranceCode: healthInsurance.healthInsuranceId,
      personCode: String(PseudonymAppointmentModel.stepOne.currentPatientSelected.id),
      planCode: healthInsurance.planId ?? '',
      referenceCompanyCode: PseudonymAppointmentModel.referenceCompanyCode ?? '',
    };

    PseudonymAppointmentModel.stepCheckupSuggestions.checkupRequisitionParameters = body;

    return HttpService.post(APPLICATION_ENDPOINTS.GET_CHECKUP_LIST, {}, { body }).then(response => {
      const checkupSuggestionGroups = response.body as CheckupSuggestionGroups;

      PseudonymAppointmentModel.stepCheckupSuggestions.checkupSuggestionGroups = checkupSuggestionGroups;

      setTimeout(() => {
        this.panelSuggestionListApi.setVisible(true);
      });
    });
  }

  @OnMount({ target: PseudonymAppointmentsStepCheckupSuggestionsAliases.DATE_PICKER_SCHEDULE_RANGE })
  handleMountDatePickerCheckupScheduleRange(event: MountEvent): void {
    if (!event || !PseudonymAppointmentModel.stepTwo.hasCheckup) return;

    const checkupSuggestionGroups = PseudonymAppointmentModel.stepCheckupSuggestions.checkupSuggestionGroups;
    this.setValueDatePicker(checkupSuggestionGroups);
  }

  private setValueDatePicker(checkupSuggestionGroups: CheckupSuggestionGroups): void {
    const orderedCheckupTimeItemList = this.orderCheckupTimeItemList(checkupSuggestionGroups.checkupTimeItemList);

    const mapDatePicker = new Map();
    this.datePickerCheckupScheduleRangeApi.clearRanges();
    orderedCheckupTimeItemList.forEach(CheckupTimeItem => {
      if (!mapDatePicker.has(CheckupTimeItem.expectedDate)) {
        this.datePickerCheckupScheduleRangeApi.addRange(CheckupTimeItem.expectedDate, CheckupTimeItem.expectedDate);
        mapDatePicker.set(CheckupTimeItem.expectedDate, true);
      }
    });
    const startDate = orderedCheckupTimeItemList[0].expectedDate;

    this.datePickerCheckupScheduleRangeApi.setInitialDate(startDate);
    this.datePickerCheckupScheduleRangeApi.setValue(startDate);
  }

  private orderCheckupTimeItemList(checkupTimeItemList: CheckupSuggestion[]): CheckupSuggestion[] {
    return checkupTimeItemList.sort((a, b) => new Date(a.expectedDate).getTime() - new Date(b.expectedDate).getTime());
  }

  @OnChange({ target: PseudonymAppointmentsStepCheckupSuggestionsAliases.DATE_PICKER_SCHEDULE_RANGE })
  handleChangeDatePickerCheckupScheduleRange(event: ChangeEvent<string>): void {
    if (!event?.value || !PseudonymAppointmentModel.stepTwo.hasCheckup) return;

    event.target.setValue(event.value);

    const checkupTimeItem = this.filterItemsByDate(event.value);
    const formattedCheckupTimeItem = this.formatExpectedDate(checkupTimeItem);
    this.setDeckInformation(this.checkupSuggestionMapperApi, formattedCheckupTimeItem);
  }

  @OnMount({ target: PseudonymAppointmentsStepCheckupSuggestionsAliases.DECK_SUGGESTION_GROUPS })
  handleMountFields(event: MountEvent): void {
    if (!event || !PseudonymAppointmentModel.stepTwo.hasCheckup) return;

    const firstCheckupDate = PseudonymAppointmentModel.stepCheckupSuggestions.checkupSuggestionGroups.checkupTimeItemList[0].expectedDate;
    const checkupTimeItem = this.filterItemsByDate(firstCheckupDate);
    const formattedCheckupTimeItem = this.formatExpectedDate(checkupTimeItem);
    this.setDeckInformation(this.checkupSuggestionMapperApi, formattedCheckupTimeItem);
    this.formatExpectedDate(checkupTimeItem);
  }

  private setDeckInformation(mapper: MapperAPI, checkupTimeItemList: CheckupSuggestion[]): void {
    mapper.setData(checkupTimeItemList as unknown as Array<Record<string, Value>>);
  }

  private filterItemsByDate(date: string): CheckupSuggestion[] {
    const checkupSuggestionGroups = PseudonymAppointmentModel.stepCheckupSuggestions.checkupSuggestionGroups;
    return checkupSuggestionGroups.checkupTimeItemList.filter(item => moment.utc(item.expectedDate).isSame(moment.utc(date), 'day'));
  }

  private formatExpectedDate(checkupTimeItem: CheckupSuggestion[]): CheckupSuggestion[] {
    const formattedCheckupTimeItem = JSON.parse(JSON.stringify(checkupTimeItem)) as CheckupSuggestion[];
    formattedCheckupTimeItem.forEach(item => {
      item.expectedDate = moment(item.expectedDate).format('HH:mm');
    });
    return formattedCheckupTimeItem;
  }

  @OnSelect({ target: PseudonymAppointmentsStepCheckupSuggestionsAliases.CARD_CONTAINER_SUGGESTION })
  handleCheckupDeckItemClick(event: SelectEvent<CardContainerAPI>): void {
    if (!event?.target || !PseudonymAppointmentModel.stepTwo.hasCheckup) return;

    this.setWizardStepDone(AppointmentsSteps.STEP_CHECKUP_SUGGESTION, event.selected);
    let selectedSuggestion = {} as CheckupSuggestion;

    if (event.selected) {
      const suggestionCard = event.target as unknown as CardContainerAPI;
      selectedSuggestion = suggestionCard.getData() as unknown as CheckupSuggestion;
    }

    const checkupList = PseudonymAppointmentModel.stepCheckupSuggestions.checkupSuggestionGroups.checkupTimeItemList;
    const checkupItem = checkupList.find(checkup =>
      moment(checkup.expectedDate).format('YYYY-MM-DD HH:mm:ss') + '-' + checkup.sector === selectedSuggestion.uniqueId
    );

    if (checkupItem) {
      selectedSuggestion = checkupItem;
    }

    PseudonymAppointmentModel.stepCheckupSuggestions.selectedSuggestion = selectedSuggestion;

    const scheduleItem = this.populateSuggestionItem();
    PseudonymAppointmentModel.stepCheckupSuggestions.scheduleItem = selectedSuggestion.sector ? scheduleItem: [];
  }

  private populateSuggestionItem(): ScheduleItem[] {
    const day = moment(PseudonymAppointmentModel.stepCheckupSuggestions.selectedSuggestion.expectedDate);
    const scheduleItem = [{
      appointmentType: ScheduleType.CHECKUP,
      dsEstablishment: PseudonymAppointmentModel.stepCheckupSuggestions.selectedSuggestion.establishment,
      dsProcInterno: PseudonymAppointmentModel.stepCheckupSuggestions.selectedSuggestion.sector,
      startDate: day,
      subtitle1: PseudonymAppointmentModel.stepTwo.procedures[0].checkupTypeDescription,
    }] as unknown as ScheduleItem[];
    return scheduleItem;
  }
}
